/**
 * No dimensions in this scope yet: `POST /seed` fills the tenant's ontology from the platform's
 * default model. `baseline_approved` names whether that model itself is still waiting on an
 * administrator elsewhere in the estate -- shown as a note, never a block: the button still
 * seeds this organisation's own copy either way.
 */
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { ErrorState } from '../../../chrome/state';
import { Button } from '../../../ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '../../../ui/empty';
import { useSeedOntology } from './api/queries';
import type { OntologyScope } from './api/schemas';
import { om, omPlural } from './messages';

export function SeedEmptyState(props: { tenantId: string; scope: OntologyScope }): ReactNode {
  const seedMutation = useSeedOntology(props.tenantId, props.scope);
  const [note, setNote] = useState<string | null>(null);

  function handleSeed(): void {
    seedMutation.mutate(undefined, {
      onSuccess: (result) => {
        const message = omPlural('empty.seeded.other', result.added, { n: result.added });
        toast.success(message);
        setNote(result.baseline_approved ? null : om('empty.awaitingApproval'));
      },
    });
  }

  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>{om('empty.title')}</EmptyTitle>
        {note !== null && <EmptyDescription>{note}</EmptyDescription>}
      </EmptyHeader>
      {seedMutation.error !== null && <ErrorState failure={seedMutation.error.failure} />}
      <Button type="button" onClick={handleSeed} disabled={seedMutation.isPending}>
        {om('empty.seed')}
      </Button>
    </Empty>
  );
}
